// Enhanced SecondaryDomainActivities main page - Modern, professional design
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ActivityList from './components/ActivityList';
import Layout from "../../Layout";
import { API_CONFIG } from '../../config/api';

const SecondaryDomainActivities = () => {
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedGrade, setSelectedGrade] = useState('all');
    const navigate = useNavigate();

    useEffect(() => {
        fetchActivities();
    }, []);

    const fetchActivities = async () => {
        try {
            setLoading(true);
            setError(null);

            const token = localStorage.getItem('accessToken');
            const apiUrl = API_CONFIG.BASE_URL;
            const response = await axios.get(
                `${apiUrl}/secondary/user/domain-activities`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            setActivities(response.data);
        } catch (err) {
            console.error('Error fetching activities:', err);
            setError(err.response?.data?.detail || 'Failed to load activities');
        } finally {
            setLoading(false);
        }
    };

    const handleActivityClick = (activityId) => {
        navigate(`/secondary-domain-activities/${activityId}`);
    };

    // Filter activities by grade
    const filteredActivities = selectedGrade === 'all'
        ? activities
        : activities.filter(activity => {
            const gradeMatch = activity.class_name?.match(/\d+/);
            return gradeMatch && gradeMatch[0] === selectedGrade;
        });

    return (
        <Layout>
            <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-orange-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <h1 className="text-4xl md:text-5xl font-bold text-[#1D3469] mb-3">
                            Secondary Domain Activities
                        </h1>
                        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                            Group Project Work and Domain-Based Learning
                        </p>
                    </div>

                    {/* Grade Filter Tabs */}
                    <div className="mb-8">
                        <div className="bg-white rounded-2xl shadow-lg p-4 backdrop-blur-lg bg-opacity-90">
                            <div className="flex flex-wrap gap-3 justify-center">
                                {['all', '9', '10', '11', '12'].map((grade) => (
                                    <button
                                        key={grade}
                                        className={`px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 ${selectedGrade === grade
                                                ? 'bg-gradient-to-r from-[#D67C2C] to-[#F17C45] text-white shadow-lg transform scale-105'
                                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:scale-105'
                                            }`}
                                        onClick={() => setSelectedGrade(grade)}
                                    >
                                        {grade === 'all' ? 'All Classes' : `Grade ${grade}`}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Content Area */}
                    <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8 backdrop-blur-lg bg-opacity-95">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-20">
                                <div className="relative">
                                    <div className="w-16 h-16 border-4 border-gray-200 border-t-[#D67C2C] rounded-full animate-spin"></div>
                                    <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-[#F17C45] rounded-full animate-spin" style={{ animationDirection: 'reverse', animationDuration: '1.5s' }}></div>
                                </div>
                                <p className="mt-6 text-gray-600 font-medium">Loading your activities...</p>
                            </div>
                        ) : error ? (
                            <div className="flex flex-col items-center justify-center py-20">
                                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
                                    <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                                <p className="text-red-600 font-semibold text-lg mb-4">{error}</p>
                                <button
                                    onClick={fetchActivities}
                                    className="px-6 py-3 bg-gradient-to-r from-[#D67C2C] to-[#F17C45] text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                                >
                                    Try Again
                                </button>
                            </div>
                        ) : filteredActivities.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20">
                                <div className="w-24 h-24 bg-gradient-to-br from-orange-100 to-blue-100 rounded-full flex items-center justify-center mb-6">
                                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                </div>
                                <h3 className="text-2xl font-bold text-gray-700 mb-2">
                                    No Activities Yet
                                </h3>
                                <p className="text-gray-500 text-center max-w-md">
                                    {selectedGrade === 'all'
                                        ? 'Your teacher hasn\'t created any domain activities yet. Check back soon!'
                                        : `No activities found for Grade ${selectedGrade}. Try selecting a different grade.`}
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* Stats Summary */}
                                <div className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="text-sm text-blue-600 font-medium">Total Activities</p>
                                                <p className="text-2xl font-bold text-blue-900">{filteredActivities.length}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="text-sm text-green-600 font-medium">Completed</p>
                                                <p className="text-2xl font-bold text-green-900">
                                                    {filteredActivities.filter(a => a.completion_status === 'completed').length}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <div>
                                                <p className="text-sm text-orange-600 font-medium">In Progress</p>
                                                <p className="text-2xl font-bold text-orange-900">
                                                    {filteredActivities.filter(a => a.completion_status === 'in_progress').length}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Activity List */}
                                <ActivityList
                                    activities={filteredActivities}
                                    onActivityClick={handleActivityClick}
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default SecondaryDomainActivities;
